import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import {
  Camera,
  ChefHat,
  ClipboardList,
  ChevronDown,
  ShieldCheck,
  Sparkles,
  UtensilsCrossed,
} from "lucide-react";

const LANGS = /** @type {const} */ ({
  en: { label: "English", countryCode: "GB", dir: "ltr", htmlLang: "en" },
  ar: { label: "العربية", countryCode: "BH", dir: "rtl", htmlLang: "ar-BH" },
});

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function setDocumentLangDir({ lang, dir }) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
  document.documentElement.dir = dir;
}

function ImageWithFallback({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={cx(
          "relative overflow-hidden rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white",
          className
        )}
        aria-label={alt}
        role="img"
      >
        <div className="absolute inset-0 opacity-[0.08] [background-image:radial-gradient(#059669_1px,transparent_1px)] [background-size:18px_18px]" />
        <div className="relative flex h-full w-full items-center justify-center">
          <div className="flex items-center gap-3 rounded-xl bg-white/80 px-5 py-3 text-emerald-900 shadow-sm ring-1 ring-emerald-100">
            <Sparkles className="h-5 w-5" />
            <span className="text-sm font-semibold">Image placeholder</span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function App() {
  const [lang, setLang] = useState("en");
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  const t = useMemo(() => {
    const copy = {
      en: {
        brand: "ChefCookAI",
        navCta: "Get recipes",
        heroKicker: "AI chef for your everyday kitchen",
        heroTitle: "Take a photo. Get personal recipes. Cook with confidence.",
        heroSubtitle:
          "Snap your fridge or pantry and let ChefCookAI turn what you already have into delicious, allergy-aware recipes with full nutrition info.",
        heroPrimary: "Take a photo",
        heroSecondary: "See how it works",
        trustLine: "Designed for busy home cooks in Bahrain.",
        sections: [
          {
            icon: Camera,
            eyebrow: "Take a photo",
            title: "Photograph your fridge or pantry",
            body:
              "You don't have to manually list ingredients — our powerful AI recognizes what you have in seconds.",
            imageAlt: "A phone taking a photo of a fridge full of ingredients",
            imageSrc: "/images/scan-fridge.jpg",
          },
          {
            icon: ClipboardList,
            eyebrow: "10 minutes a day",
            title: "Get personalized suggestions",
            body:
              "Based on the ingredients you upload, ChefCookAI creates personalized recipes just for you. We also take your allergies into account, and include complete nutritional information.",
            imageAlt: "A clean app UI showing personalized recipes and nutrition",
            imageSrc: "/images/recipe-card.jpg",
          },
          {
            icon: ShieldCheck,
            eyebrow: "Daily benefit",
            title: "Cook with confidence",
            body:
              "Each recipe comes with simple, step-by-step instructions — great for beginners and experienced cooks alike. Our AI also helps you maximize what you already have to reduce food waste.",
            imageAlt: "A confident home cook following step-by-step recipe instructions",
            imageSrc: "/images/hero-ai-chef.jpg",
          },
        ],
        extra: {
          eyebrow: "10 minutes a day",
          title: "Shop smarter",
          body:
            "ChefCookAI doesn't just stop at meal ideas. If you're missing something, the app suggests alternatives or builds a simplified shopping list — so you only buy what you really need.",
        },
        footerTitle: "Welcome to SouschAIf, your AI-Powered Kitchen Companion.",
        footerBody:
          "SouschAIf is a subscription service. Take the first step toward a smarter, more sustainable kitchen. Activate Souschaif and see how easy it is to turn everyday ingredients into incredible meals and reduce food waste and save money at the same time! By subscribing to the service, you are accepting all Terms & Conditions of the service. The subscription will be automatically renewed and your account will be debited with BHD 0.25/day (Zain), BHD 0.30/day (STC), BHD 0.30/day (Batelco) (subject to 10% VAT) charging cycle until you unsubscribe. You can unsubscribe from this service, anytime, by sending UNSUB SC to 88888 (Zain), UNSUB SC to 98054 (STC) or UNSUB SC to 94398 (Batelco). To make use of this service you must be more than 18 years old or have received permission from your parents or a person who is authorised to pay your bill. Data charges apply for browsing and downloading contents on this portal. SouschAIf is provided to you by: Mobixas B.V.",
        legal: { terms: "Terms", privacy: "Privacy" },
      },
      ar: {
        brand: "ChefCookAI",
        navCta: "احصل على وصفات",
        heroKicker: "شيف ذكاء اصطناعي لمطبخك اليومي",
        heroTitle: "التقط صورة. احصل على وصفات شخصية. واطبخ بثقة.",
        heroSubtitle:
          "صوّر الثلاجة أو خزانة المؤن، ودع ChefCookAI يحوّل المكوّنات المتوفرة لديك إلى وصفات لذيذة تراعي الحساسية مع معلومات غذائية كاملة.",
        heroPrimary: "التقط صورة",
        heroSecondary: "شاهد كيف يعمل",
        trustLine: "مصمم للطهاة المنزليين المشغولين في البحرين.",
        sections: [
          {
            icon: Camera,
            eyebrow: "التقط صورة",
            title: "صوّر الثلاجة أو خزانة المؤن",
            body:
              "لا حاجة لكتابة المكوّنات يدويًا — يتعرّف ذكاؤنا الاصطناعي على ما لديك خلال ثوانٍ.",
            imageAlt: "هاتف يلتقط صورة لثلاجة مليئة بالمكوّنات",
            imageSrc: "/images/scan-fridge.jpg",
          },
          {
            icon: ClipboardList,
            eyebrow: "10 دقائق يوميًا",
            title: "اقتراحات ووصفات مخصصة لك",
            body:
              "بناءً على المكوّنات التي ترفعها، ينشئ ChefCookAI وصفات شخصية لك. كما يأخذ الحساسية بعين الاعتبار ويعرض معلومات غذائية كاملة.",
            imageAlt: "واجهة تطبيق تعرض وصفات مخصصة ومعلومات غذائية",
            imageSrc: "/images/recipe-card.jpg",
          },
          {
            icon: ShieldCheck,
            eyebrow: "فائدة يومية",
            title: "اطبخ بثقة",
            body:
              "كل وصفة تأتي بخطوات سهلة وواضحة — مناسبة للمبتدئين والمحترفين. كما يساعدك الذكاء الاصطناعي على الاستفادة من المكوّنات الموجودة لتقليل هدر الطعام.",
            imageAlt: "طاهٍ منزلي يتبع خطوات وصفة بسهولة وثقة",
            imageSrc: "/images/hero-ai-chef.jpg",
          },
        ],
        extra: {
          eyebrow: "10 دقائق يوميًا",
          title: "تسوّق بذكاء",
          body:
            "ChefCookAI لا يتوقف عند أفكار الوجبات. إذا كان ينقصك شيء، سيقترح بدائل أو ينشئ قائمة تسوق مبسطة — لتشتري فقط ما تحتاجه فعلاً.",
        },
        footerTitle: "مرحبًا بك في SouschAIf، رفيق المطبخ المدعوم بالذكاء الاصطناعي.",
        footerBody:
          "SouschAIf خدمة اشتراك. اتخذ الخطوة الأولى نحو مطبخ أذكى وأكثر استدامة. فعّل Souschaif واكتشف مدى سهولة تحويل المكوّنات اليومية إلى وجبات رائعة وتقليل هدر الطعام وتوفير المال في الوقت نفسه! بالاشتراك في الخدمة، فإنك توافق على جميع الشروط والأحكام الخاصة بالخدمة. سيتم تجديد الاشتراك تلقائيًا وسيتم خصم BHD 0.25/يوم (زين)، BHD 0.30/يوم (STC)، BHD 0.30/يوم (بتلكو) (خاضعة لـ 10% ضريبة القيمة المضافة) حتى تقوم بإلغاء الاشتراك. يمكنك إلغاء الاشتراك في أي وقت بإرسال UNSUB SC إلى 88888 (زين) أو UNSUB SC إلى 98054 (STC) أو UNSUB SC إلى 94398 (بتلكو). لاستخدام هذه الخدمة يجب أن يكون عمرك أكثر من 18 عامًا أو أن تحصل على إذن من والديك أو من شخص مخوّل بدفع فاتورتك. تُطبق رسوم البيانات عند التصفح وتنزيل المحتوى عبر هذه البوابة. يتم تقديم SouschAIf إليك من قبل: Mobixas B.V.",
        legal: { terms: "الشروط", privacy: "الخصوصية" },
      },
    };
    return copy[lang];
  }, [lang]);

  const langMeta = LANGS[lang];
  const isRtl = langMeta.dir === "rtl";

  useEffect(() => {
    setDocumentLangDir({ lang: langMeta.htmlLang, dir: langMeta.dir });
  }, [langMeta.htmlLang, langMeta.dir]);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") setLangMenuOpen(false);
    }
    function onPointerDown(e) {
      if (!langMenuRef.current) return;
      if (!langMenuRef.current.contains(e.target)) setLangMenuOpen(false);
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);

  return (
    <div className={cx("min-h-screen", isRtl ? "font-arabic" : "font-body")}>
      <div className="bg-gradient-to-br from-emerald-50 via-white to-white">
        <header className="mx-auto max-w-6xl px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                <ChefHat className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="font-heading text-base font-bold text-emerald-950">
                  {t.brand}
                </div>
                <div className="text-xs text-slate-600">{t.heroKicker}</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative" ref={langMenuRef}>
                <button
                  type="button"
                  onClick={() => setLangMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm hover:bg-emerald-50"
                  aria-haspopup="menu"
                  aria-expanded={langMenuOpen}
                >
                  <ReactCountryFlag
                    countryCode={langMeta.countryCode}
                    svg
                    style={{
                      width: "1.2em",
                      height: "1.2em",
                      borderRadius: "999px",
                    }}
                    aria-label={langMeta.label}
                  />
                  <span>{langMeta.label}</span>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </button>

                {langMenuOpen ? (
                  <div
                    role="menu"
                    className={cx(
                      "absolute z-20 mt-2 min-w-[210px] overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-lg",
                      isRtl ? "left-0" : "right-0"
                    )}
                  >
                    {Object.entries(LANGS).map(([key, cfg]) => {
                      const active = key === lang;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setLang(key);
                            setLangMenuOpen(false);
                          }}
                          className={cx(
                            "flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-slate-800 transition",
                            active
                              ? "bg-emerald-50"
                              : "hover:bg-emerald-50/70"
                          )}
                        >
                          <ReactCountryFlag
                            countryCode={cfg.countryCode}
                            svg
                            style={{
                              width: "1.2em",
                              height: "1.2em",
                              borderRadius: "999px",
                            }}
                            aria-label={cfg.label}
                          />
                          <span className="flex-1 text-left">{cfg.label}</span>
                          {active ? (
                            <span className="text-xs font-bold text-emerald-700">
                              {isRtl ? "محدد" : "Selected"}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <a
                href="/s"
                className="hidden rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 sm:inline-block"
              >
                {t.navCta}
              </a>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-4 pb-14 pt-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm font-bold text-emerald-900">
                <Sparkles className="h-4 w-4" />
                <span>{t.heroKicker}</span>
              </div>

              <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                {t.heroTitle}
              </h1>
              <p className="text-lg leading-relaxed text-slate-700">
                {t.heroSubtitle}
              </p>

              <div id="cta" className="flex flex-wrap items-center gap-3">
                <a
                  href="/s"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-base font-extrabold text-white shadow-sm hover:bg-emerald-700"
                >
                  <Camera className="h-5 w-5" />
                  {t.heroPrimary}
                </a>
                <a
                  href="/s"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-white px-5 py-3 text-base font-extrabold text-emerald-900 shadow-sm hover:bg-emerald-50"
                >
                  <UtensilsCrossed className="h-5 w-5" />
                  {t.heroSecondary}
                </a>
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-600">
                <ShieldCheck className="h-4 w-4 text-emerald-700" />
                <span>{t.trustLine}</span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 -z-10 rounded-[28px] bg-gradient-to-br from-emerald-200/40 to-transparent blur-2xl" />
              <ImageWithFallback
                src="/images/hero-ai-chef.jpg"
                alt="AI chef helping with cooking suggestions"
                className="h-[420px] w-full rounded-[28px] object-cover shadow-lg ring-1 ring-emerald-100"
              />
              <div
                className={cx(
                  "absolute bottom-5 rounded-2xl bg-white/90 p-4 shadow-lg ring-1 ring-emerald-100 backdrop-blur",
                  isRtl ? "left-5" : "right-5"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white">
                    <ChefHat className="h-5 w-5" />
                  </div>
                  <div className="leading-tight">
                    <div className="text-sm font-extrabold text-slate-900">
                      ChefCookAI
                    </div>
                    <div className="text-xs text-slate-600">
                      {isRtl
                        ? "وصفات حسب مكوّناتك"
                        : "Recipes based on your ingredients"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <section id="features" className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-10">
          {t.sections.map((s, idx) => {
            const Icon = s.icon;
            const reversed = idx % 2 === 1;
            return (
              <div
                key={s.title}
                className={cx(
                  "grid items-center gap-8 rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm md:p-10 lg:grid-cols-2",
                  reversed ? "lg:[&>*:first-child]:order-2" : ""
                )}
              >
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm font-bold text-emerald-900">
                    <Icon className="h-4 w-4" />
                    <span>{s.eyebrow}</span>
                  </div>
                  <h2 className="font-heading text-2xl font-extrabold text-slate-900">
                    {s.title}
                  </h2>
                  <p className="text-base leading-relaxed text-slate-700">
                    {s.body}
                  </p>
                </div>

                <ImageWithFallback
                  src={s.imageSrc}
                  alt={s.imageAlt}
                  className="h-[280px] w-full rounded-2xl object-cover ring-1 ring-emerald-100"
                />
              </div>
            );
          })}
        </div>
      </section>

      <section id="how" className="bg-gradient-to-br from-emerald-50 via-white to-white">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <div className="rounded-3xl border border-emerald-100 bg-white p-8 shadow-sm md:p-10">
            <div className="grid gap-8 lg:grid-cols-3">
              <div className="space-y-2 lg:col-span-1">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm font-bold text-emerald-900">
                  <ClipboardList className="h-4 w-4" />
                  <span>{t.extra.eyebrow}</span>
                </div>
                <h2 className="font-heading text-2xl font-extrabold text-slate-900">
                  {t.extra.title}
                </h2>
              </div>
              <div className="lg:col-span-2">
                <p className="text-base leading-relaxed text-slate-700">
                  {t.extra.body}
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {[
                    {
                      icon: Camera,
                      title: isRtl ? "صورة واحدة" : "One photo",
                      body: isRtl
                        ? "التقط صورة لمكوّناتك."
                        : "Snap your ingredients.",
                    },
                    {
                      icon: Sparkles,
                      title: isRtl ? "وصفات فورية" : "Instant recipes",
                      body: isRtl
                        ? "اقتراحات مخصصة بناءً على ما لديك."
                        : "Tailored suggestions from what you have.",
                    },
                    {
                      icon: ClipboardList,
                      title: isRtl ? "قائمة تسوق" : "Smart list",
                      body: isRtl
                        ? "بدائل وقائمة تسوق مبسطة."
                        : "Alternatives + simplified shopping list.",
                    },
                  ].map((card) => {
                    const Icon = card.icon;
                    return (
                      <div
                        key={card.title}
                        className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm"
                      >
                        <div className="flex items-center gap-2 text-emerald-900">
                          <Icon className="h-5 w-5 text-emerald-700" />
                          <div className="text-sm font-extrabold">
                            {card.title}
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-slate-700">
                          {card.body}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-emerald-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-1">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600 text-white">
                  <ChefHat className="h-5 w-5" />
                </div>
                <div className="font-heading text-base font-extrabold text-slate-900">
                  SouschAIf
                </div>
              </div>
              <div className="text-sm font-bold text-slate-900">
                {t.footerTitle}
              </div>
              <div className="flex items-center gap-4 text-sm font-semibold text-emerald-800">
                <a href="/terms.html" className="hover:underline">
                  {t.legal.terms}
                </a>
                <a href="/privacy.html" className="hover:underline">
                  {t.legal.privacy}
                </a>
              </div>
            </div>

            <div className="text-sm leading-relaxed text-slate-700 lg:col-span-2">
              {t.footerBody}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}


